import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTemplates } from '../../hooks/useTemplates';
import { useConfirm } from '../../hooks/useConfirm';
import { templateService } from '../../services/templateService';
import { TemplateList } from './TemplateList';
import { TemplateEditor } from './TemplateEditor';
import type { ContractTemplate } from '@shared/types';

export function ContractTemplates() {
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<ContractTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const { templates, loading, reload } = useTemplates({
    type: typeFilter as any,
  });

  const { confirm, ConfirmDialog } = useConfirm();

  const handleCreateNew = () => {
    setSelectedTemplate(null);
    setIsCreating(true);
  };

  const handleEdit = (template: ContractTemplate) => {
    setSelectedTemplate(template);
    setIsCreating(false);
  };

  const handleSave = async (template: Partial<ContractTemplate>) => {
    setSaving(true);
    const toastId = toast.loading(selectedTemplate ? 'Updating template...' : 'Creating template...');
    
    try {
      if (selectedTemplate) {
        await templateService.updateTemplate(selectedTemplate.id, template);
        toast.success('Template updated successfully!', { id: toastId });
      } else {
        await templateService.createTemplate(template);
        toast.success('Template created successfully!', { id: toastId });
      }
      setSelectedTemplate(null);
      setIsCreating(false);
      reload();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template.', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    const confirmed = await confirm({
      title: `${action === 'activate' ? 'Activate' : 'Deactivate'} Template`,
      message: `Are you sure you want to ${action} this template?`,
      confirmText: action === 'activate' ? 'Activate' : 'Deactivate',
      variant: action === 'deactivate' ? 'warning' : 'info',
    });

    if (!confirmed) return;
    
    const toastId = toast.loading(`${action === 'activate' ? 'Activating' : 'Deactivating'} template...`);
    
    try {
      await templateService.updateTemplate(id, { isActive: !currentStatus });
      toast.success(`Template ${action}d successfully!`, { id: toastId });
      reload();
    } catch (error) {
      console.error('Failed to toggle template:', error);
      toast.error(`Failed to ${action} template.`, { id: toastId });
    }
  };

  const handleCancel = () => {
    setSelectedTemplate(null);
    setIsCreating(false);
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Template List */}
        <div>
          <TemplateList
            templates={templates}
            loading={loading}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            onCreateNew={handleCreateNew}
            onEdit={handleEdit}
            onToggleActive={handleToggleActive}
          />
        </div>

        {/* Template Editor */}
        <div>
          {(selectedTemplate || isCreating) && (
            <TemplateEditor
              template={selectedTemplate}
              onSave={handleSave}
              onCancel={handleCancel}
              saving={saving}
            />
          )}
          {!selectedTemplate && !isCreating && (
            <div className="bg-white shadow-sm rounded-lg p-8 text-center text-gray-500">
              Select a template to edit or create a new one
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog />
    </>
  );
}

